import { getUserFriendlyError } from "@/lib/user-error";

const parseDetailsMessage = (details: unknown): string | null => {
  if (!details) return null;

  if (typeof details === "string") {
    const trimmed = details.trim();
    if (!trimmed || trimmed === "[object Object]") return null;

    try {
      const parsed = JSON.parse(trimmed) as unknown;
      return resolveErrorMessage(parsed);
    } catch {
      return trimmed;
    }
  }

  if (typeof details === "object") {
    return resolveErrorMessage(details);
  }

  return null;
};

const resolveErrorMessage = (error: unknown): string | null => {
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  if (!error || typeof error !== "object") return null;

  const obj = error as Record<string, unknown>;

  const direct = [obj.message, obj.error, obj.description];
  for (const candidate of direct) {
    if (typeof candidate === "string" && candidate.trim()) return candidate;
    if (candidate instanceof Error && candidate.message.trim()) return candidate.message;
  }

  if (typeof obj.error === "object" && obj.error !== null) {
    const nested = resolveErrorMessage(obj.error);
    if (nested) return nested;
  }

  if (typeof obj.cause === "object" && obj.cause !== null) {
    const nested = resolveErrorMessage(obj.cause);
    if (nested) return nested;
  }

  if ("details" in obj) {
    const fromDetails = parseDetailsMessage(obj.details);
    if (fromDetails) return fromDetails;
  }

  if (typeof obj.context === "object" && obj.context !== null) {
    const context = obj.context as Record<string, unknown>;
    if ("details" in context) {
      const fromContextDetails = parseDetailsMessage(context.details);
      if (fromContextDetails) return fromContextDetails;
    }
    if ("error" in context) {
      const fromContextError = resolveErrorMessage(context.error);
      if (fromContextError) return fromContextError;
    }
  }

  return null;
};

export const getEdgeFunctionErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  const nestedMessage = resolveErrorMessage(error);
  if (nestedMessage) return getUserFriendlyError(nestedMessage, fallback);
  return getUserFriendlyError(error, fallback);
};
